import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import { Box, Flex } from '@rebass/grid';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useTenantContext } from '../../context/TenantContext';
import { Customer, useMyQuery } from '../../generated/graphql';
import withAuth from '../../hoc/withAuth';
import useAnalyticsContentCategory from '../../hooks/analytics/useAnalyticsContentCategory';
import { isUpcoming } from '../../utils/order';
import ContentWrapper from '../ContentWrapper';
import Heading from '../designsystem/Heading';
import Spinner from '../designsystem/Spinner';
import Page from '../Page';
import TabNavigation from '../TabNavigation';
import Orders from '../Orders';
import Actions from './My/Actions';
import { getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import TabNavigationContent from '../TabNavigationContent';
import BookingHistory from './My/BookingHistory';
import SectionTitleWrapper from './My/SectionTitleWrapper';
import UserSettings from './My/UserSettings';
import BookingReviews from './My/BookingReviews';
import pick from '../../utils/pick';
import EmptyState from './My/EmptyState';
import Anchor from '../designsystem/Anchor';
import Stack from '../Stack';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import FavoriteTile from '../FavoriteTile';
import TextButton from '../designsystem/TextButton';
import Favorites, { myFavoritesFragmentToProps } from './My/Favorites';
import Link from 'next/link';
import { createEnumParam, useQueryParam, withDefault } from 'use-query-params';
import ActivityReviews from './My/ActivityReviews';

export const query = gql`
    query My {
        viewer {
            ...MyCustomer
        }
    }

    fragment MyCustomer on Customer {
        id
        firstName
        lastName
        birthdate
        email
        address {
            ...Address
        }
        phoneNumber
        orders {
            ...MyOrder
        }
        favorites {
            ...MyFavorites
        }
    }

    fragment MyFavorites on Favorite {
        id
        ... on FavoriteRentalUnit {
            rentalUnit {
                slug
                name
                id
                jpegThumbnail: listImage {
                    transform(config: TILE, format: JPEG) {
                        ...Transform
                    }
                }
                webpThumbnail: listImage {
                    transform(config: TILE, format: WEBP) {
                        ...Transform
                    }
                }
            }
        }

        ... on FavoriteCompany {
            company {
                slug
                name
                id
                jpegThumbnail: listImage {
                    transform(config: TILE, format: JPEG) {
                        ...Transform
                    }
                }
                webpThumbnail: listImage {
                    transform(config: TILE, format: WEBP) {
                        ...Transform
                    }
                }
            }
        }

        ... on FavoriteActivity {
            activity {
                slug
                name
                id
                jpegThumbnail: listImage {
                    transform(config: TILE, format: JPEG) {
                        ...Transform
                    }
                }
                webpThumbnail: listImage {
                    transform(config: TILE, format: WEBP) {
                        ...Transform
                    }
                }
            }
        }
    }
`;

const customerValueKeys = ['address', 'birthdate', 'firstName', 'lastName', 'phoneNumber', 'email'] as const;
export type CustomerValues = Pick<Customer, (typeof customerValueKeys)[number]>;

const selectedTabs = ['dashboard', 'favorites', 'bookingReviews', 'activityReviews', 'history', 'settings'] as const;
export type SelectedTab = typeof selectedTabs;
export const SelectedTabParam = createEnumParam(selectedTabs as Writeable<SelectedTab>);

const My = () => {
    const { data, loading } = useMyQuery();
    const { brandConfig } = useTenantContext();
    const intl = useIntl();
    const [, setSelectedTab] = useQueryParam('selectedTab', withDefault(SelectedTabParam, 'dashboard'));
    useAnalyticsContentCategory('my');

    if (loading || !data?.viewer) {
        return (
            <Spinner
                message={<FormattedMessage defaultMessage="Mijn {island}" values={{ island: brandConfig.name }} />}
            />
        );
    }

    const { viewer } = data;
    const { orders } = viewer;

    const upcomingOrders = orders.filter(order => order.orderItems.every(item => isUpcoming(item)));
    const customerValues = pick<CustomerValues>(viewer, customerValueKeys);

    const favorites = data?.viewer?.favorites ?? [];

    return (
        <Page>
            <HeaderWrapper>
                <StyledWrapper>
                    <Flex
                        flexDirection={['column', 'row']}
                        mt={[6, 5, null, null, '7.4rem']}
                        justifyContent="space-between"
                    >
                        <Heading>
                            <StyledTitle style={{ fontWeight: 400 }} variant="large">
                                <FormattedMessage defaultMessage="Welkom bij" />
                                <br />
                                <SubHeading>
                                    <FormattedMessage
                                        defaultMessage="Mijn {island}"
                                        values={{ island: brandConfig.name }}
                                    />
                                </SubHeading>
                            </StyledTitle>
                        </Heading>
                    </Flex>
                </StyledWrapper>
            </HeaderWrapper>
            <StyledFlex pb={8}>
                <TabNavigation>
                    <TabNavigationContent title={<FormattedMessage defaultMessage="Dashboard" />} slug="dashboard">
                        {brandConfig.checkout?.showBookingConfirmExtras && (
                            <BookingExtrasCta p={[3, null, '80px']} mt={[2, null, '64px']}>
                                <Box style={{ maxWidth: 555 }}>
                                    <Stack spacing={4}>
                                        <Title variant="large">
                                            <FormattedMessage defaultMessage="Huur een fiets op Texel" />
                                        </Title>
                                        <Body>
                                            <FormattedMessage defaultMessage="Op de fiets zie je de mooiste plekjes van de omgeving. Ga jij voor een elektrische fiets, een tandem, een mountainbike of misschien wel een hippe bakfiets? Zorg dat je je huurfiets op tijd regelt. Dat kan voor een dagje of voor je hele verblijf. Je kunt je huurfiets ophalen in de winkel van de verhuurder of laten bezorgen bij je accommodatie." />
                                        </Body>
                                        <Flex mt={3}>
                                            <Button
                                                as="a"
                                                href={intl.formatMessage({
                                                    defaultMessage:
                                                        'https://www.texel.net/nl/zien-en-doen/sportief/fietsen/fiets-huren/',
                                                })}
                                                target="_blank"
                                                rel="noreferrer noopener"
                                            >
                                                <FormattedMessage defaultMessage="Huur een fiets" />
                                            </Button>
                                        </Flex>
                                    </Stack>
                                </Box>
                            </BookingExtrasCta>
                        )}

                        {orders.length === 0 && favorites.length === 0 && (
                            <EmptyState
                                title={<FormattedMessage defaultMessage="Je dashboard is nu nog leeg" />}
                                description={
                                    <FormattedMessage
                                        defaultMessage="Begin je vakantie goed en begin met zoeken naar <activityLink>activiteiten</activityLink>, <accoLink>accommodaties</accoLink> of <businessLink>bedrijven</businessLink>."
                                        values={{
                                            activityLink: children => (
                                                <Link href="/agenda" legacyBehavior>
                                                    <Anchor>{children}</Anchor>
                                                </Link>
                                            ),
                                            accoLink: children => (
                                                <Link href="/search" legacyBehavior>
                                                    <Anchor>{children}</Anchor>
                                                </Link>
                                            ),
                                            businessLink: children => (
                                                <Link href="/companies" legacyBehavior>
                                                    <Anchor>{children}</Anchor>
                                                </Link>
                                            ),
                                        }}
                                    />
                                }
                            />
                        )}
                        <Actions orders={orders} />
                        {upcomingOrders.length > 0 && (
                            <>
                                <SectionTitleWrapper>
                                    <Title elementType="h3">
                                        <FormattedMessage defaultMessage="Je aankomende vakantie(s)" />
                                    </Title>
                                </SectionTitleWrapper>
                                {upcomingOrders.map((order, i) => (
                                    <Orders key={order.id ?? i} data={order} />
                                ))}
                            </>
                        )}
                        {favorites.length > 0 && brandConfig.enableFavorites && (
                            <>
                                <SectionTitleWrapper>
                                    <Flex justifyContent="space-between" alignItems="center">
                                        <Title elementType="h3">
                                            <FormattedMessage defaultMessage="Favorieten" />
                                        </Title>
                                        <TextButton
                                            onClick={() => {
                                                setSelectedTab('favorites');
                                                setTimeout(() => {
                                                    window.scrollTo({
                                                        top: 0,
                                                        left: 0,
                                                        behavior: 'smooth',
                                                    });
                                                });
                                            }}
                                        >
                                            <FormattedMessage defaultMessage="Bekijk al je favorieten" />
                                        </TextButton>
                                    </Flex>
                                </SectionTitleWrapper>
                                <FavoritesGrid>
                                    {favorites.slice(0, 4).map(favorite => (
                                        <FavoriteTile key={favorite.id} {...myFavoritesFragmentToProps(favorite)} />
                                    ))}
                                </FavoritesGrid>
                            </>
                        )}
                    </TabNavigationContent>
                    {brandConfig.enableFavorites ? (
                        <TabNavigationContent title={<FormattedMessage defaultMessage="Favorieten" />} slug="favorites">
                            <Favorites data={favorites} />
                        </TabNavigationContent>
                    ) : null}
                    {brandConfig.hasReviews ? (
                        <>
                            <TabNavigationContent
                                title={<FormattedMessage defaultMessage="Beoordelingen" />}
                                slug="bookingReviews"
                            >
                                <BookingReviews data={orders} />
                            </TabNavigationContent>
                            <TabNavigationContent
                                title={<FormattedMessage defaultMessage="Beoordelingen" />}
                                slug="activityReviews"
                            >
                                <ActivityReviews data={orders} />
                            </TabNavigationContent>
                        </>
                    ) : null}
                    <TabNavigationContent
                        title={<FormattedMessage defaultMessage="Boekingsgeschiedenis" />}
                        slug="history"
                    >
                        <BookingHistory orders={orders} />
                    </TabNavigationContent>
                    <TabNavigationContent title={<FormattedMessage defaultMessage="Instellingen" />} slug="settings">
                        <UserSettings customerId={viewer.id!} {...customerValues} />
                    </TabNavigationContent>
                </TabNavigation>
            </StyledFlex>
        </Page>
    );
};

export default withAuth(My, { onFail: true });

const FavoritesGrid = styled.div`
    display: grid;
    row-gap: 3.2rem;
    column-gap: 3.2rem;
    grid-template-columns: repeat(1, 1fr);

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.m}) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const BookingExtrasCta = styled(Box)`
    border: 1px solid rgba(0, 0, 0, 0.17);
    border-radius: 8px;
`;
const HeaderWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.neutral['5']};
`;

const StyledTitle = styled(Title)`
    ${({ theme }) => getFontCss(theme.fontSizes.title.large)};
`;

const StyledWrapper = styled(ContentWrapper)`
    padding-top: 5.7rem;
    margin-bottom: 0;
`;

const SubHeading = styled.span`
    display: block;
    padding-top: 0.4rem;
    font-weight: 700;
`;

const StyledFlex = styled(Flex)`
    flex-direction: column;
`;
